import { useRef, useEffect, useCallback, useState } from 'react';

// Utility to calculate angle between three points (in degrees)
const calculateAngle = (p1, p2, p3) => {
  if (!p1 || !p2 || !p3) return null;
  const vector1 = { x: p1.x - p2.x, y: p1.y - p2.y, z: p1.z - p2.z };
  const vector2 = { x: p3.x - p2.x, y: p3.y - p2.y, z: p3.z - p2.z };
  const dotProduct = vector1.x * vector2.x + vector1.y * vector2.y + vector1.z * vector2.z;
  const magnitude1 = Math.sqrt(vector1.x ** 2 + vector1.y ** 2 + vector1.z ** 2);
  const magnitude2 = Math.sqrt(vector2.x ** 2 + vector2.y ** 2 + vector2.z ** 2);
  if (magnitude1 === 0 || magnitude2 === 0) return null;
  const cosTheta = Math.min(1, Math.max(-1, dotProduct / (magnitude1 * magnitude2)));
  return (Math.acos(cosTheta) * 180) / Math.PI;
};

// Utility to calculate distance between two points
const calculateDistance = (p1, p2) => {
  if (!p1 || !p2) return null;
  return Math.sqrt((p1.x - p2.x) ** 2 + (p1.y - p2.y) ** 2 + (p1.z - p2.z) ** 2);
};

// Generic yoga pose configuration
const poseConfig = {
  pose1: {
    category: 'prone',
    criticalLandmarks: [11, 12, 13, 14, 15, 16, 23, 24, 25, 26, 27, 28],
    visibilityThreshold: 0.2,
    conditions: [
      {
        type: 'position',
        check: (landmarks) => landmarks[11]?.y > 0.6 && landmarks[12]?.y > 0.6 && landmarks[23]?.y > 0.6 && landmarks[24]?.y > 0.6,
        description: 'Shoulders and hips low (prone position)'
      },
      {
        type: 'position',
        check: (landmarks) => landmarks[25]?.y < landmarks[23]?.y && landmarks[26]?.y < landmarks[24]?.y,
        description: 'Knees higher than hips'
      },
      {
        type: 'distance',
        points: [15, 27],
        maxDistance: 0.2,
        description: 'Left wrist near left ankle'
      },
      {
        type: 'distance',
        points: [16, 28],
        maxDistance: 0.2,
        description: 'Right wrist near right ankle'
      },
      {
        type: 'angle',
        points: [11, 23, 25],
        minAngle: 120,
        maxAngle: 160,
        description: 'Spine curvature'
      }
    ]
  },
  pose2: {
    category: 'standing',
    criticalLandmarks: [11, 12, 13, 14, 15, 16, 23, 24, 25, 26, 27, 28],
    visibilityThreshold: 0.2,
    conditions: [
      {
        type: 'angle',
        points: [23, 25, 27],
        maxAngle: 120,
        description: 'One knee bent'
      },
      {
        type: 'angle',
        points: [24, 26, 28],
        minAngle: 150,
        description: 'Other leg straight'
      },
      {
        type: 'position',
        check: (landmarks) => Math.abs(landmarks[13]?.y - landmarks[15]?.y) < 0.1 && Math.abs(landmarks[14]?.y - landmarks[16]?.y) < 0.1,
        description: 'Arms extended horizontally'
      }
    ]
  },
  pose3: {
    category: 'balancing',
    criticalLandmarks: [11, 12, 23, 24, 25, 26, 27, 28, 31, 32],
    visibilityThreshold: 0.2,
    conditions: [
      {
        type: 'distance',
        points: [27, 24],
        maxDistance: 0.3,
        altPoints: [28, 23],
        altMaxDistance: 0.3,
        description: 'One foot near opposite thigh or knee'
      },
      {
        type: 'angle',
        points: [23, 25, 27],
        minAngle: 150,
        altPoints: [24, 26, 28],
        altMinAngle: 150,
        description: 'Standing leg straight'
      }
    ]
  },
  pose4: {
    category: 'inverted',
    criticalLandmarks: [11, 12, 15, 16, 23, 24, 25, 26, 27, 28],
    visibilityThreshold: 0.2,
    conditions: [
      {
        type: 'position',
        check: (landmarks) => landmarks[23]?.y < landmarks[11]?.y && landmarks[24]?.y < landmarks[12]?.y,
        description: 'Hips higher than shoulders'
      },
      {
        type: 'angle',
        points: [11, 23, 25],
        minAngle: 140,
        maxAngle: 180,
        description: 'Spine-to-leg angle near straight'
      },
      {
        type: 'position',
        check: (landmarks) => landmarks[15]?.y > 0.7 && landmarks[16]?.y > 0.7,
        description: 'Hands low (near ground)'
      }
    ]
  }
};

const usePoseDetection = (webcamRef, poseLandmarkerRef, onVisibilityChange, isActive = true) => {
  const visibilityCheckIntervalRef = useRef(null);
  const landmarkHistoryRef = useRef([]);
  const lastDistanceWarningRef = useRef(0);
  const [initialCountdownActive, setInitialCountdownActive] = useState(false);
  const [isFullBodyDetected, setIsFullBodyDetected] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isCaptureComplete, setIsCaptureComplete] = useState(false);
  const countdownTimerRef = useRef(null);
  const captureTimerRef = useRef(null);
  const preliminaryCheckIntervalRef = useRef(null);

  // Smooth landmarks to reduce noise
  const smoothLandmarks = (newLandmarks, history, windowSize = 3) => {
    if (!newLandmarks || newLandmarks.length === 0) return newLandmarks;
    history.push(newLandmarks);
    if (history.length > windowSize) history.shift();

    const smoothed = newLandmarks.map((_, index) => {
      const avg = history.reduce(
        (acc, landmarks) => {
          if (!landmarks[index]) return acc;
          return {
            x: acc.x + (landmarks[index].x || 0),
            y: acc.y + (landmarks[index].y || 0),
            z: acc.z + (landmarks[index].z || 0),
            visibility: acc.visibility + (landmarks[index].visibility || 0)
          };
        },
        { x: 0, y: 0, z: 0, visibility: 0 }
      );
      const count = history.length;
      return {
        x: avg.x / count,
        y: avg.y / count,
        z: avg.z / count,
        visibility: avg.visibility / count
      };
    });
    return smoothed;
  };

  // Generic pose classifier
  const classifyPose = (landmarks) => {
    const names = {
      0: 'nose',
      7: 'left ear',
      8: 'right ear',
      11: 'left shoulder',
      12: 'right shoulder',
      13: 'left elbow',
      14: 'right elbow',
      15: 'left wrist',
      16: 'right wrist',
      23: 'left hip',
      24: 'right hip',
      25: 'left knee',
      26: 'right knee',
      27: 'left ankle',
      28: 'right ankle',
      29: 'left heel',
      30: 'right heel',
      31: 'left foot index',
      32: 'right foot index'
    };

    const criticalLandmarks = {
      frontal: [11, 12, 23, 24, 27, 28, 31, 32],
      side: [11, 12, 23, 24],
      leftSide: [11, 23, 25, 27],
      rightSide: [12, 24, 26, 28],
      bentLeftSide: [11, 13, 15, 23, 25, 27],
      bentRightSide: [12, 14, 16, 24, 26, 28],
      fullBody: [0, 7, 8, 11, 12, 13, 14, 15, 16, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32]
    };

    const getMissingLandmarks = (landmarkIndices, threshold = 0.1) => {
      return landmarkIndices.filter((index) => {
        if (!landmarks[index]) return true;
        return landmarks[index].visibility <= threshold;
      });
    };

    const isBent =
      landmarks[25]?.y > landmarks[23]?.y ||
      landmarks[26]?.y > landmarks[24]?.y ||
      landmarks[15]?.y > landmarks[11]?.y ||
      landmarks[16]?.y > landmarks[12]?.y;

    const checkPose = (pose) => {
      const missing = getMissingLandmarks(pose.criticalLandmarks, pose.visibilityThreshold);
      const maxMissing = isBent ? 4 : pose.category === 'side' ? Math.ceil(pose.criticalLandmarks.length / 2) : 2;
      if (missing.length > maxMissing) return { isDetected: false, missing };

      const allConditionsMet = pose.conditions.every((condition) => {
        if (condition.type === 'position') {
          return condition.check(landmarks);
        } else if (condition.type === 'angle') {
          const points = condition.points || condition.altPoints;
          if (!points) return false;
          const angle = calculateAngle(landmarks[points[0]], landmarks[points[1]], landmarks[points[2]]);
          if (angle === null) return false;
          const minAngle = condition.minAngle || condition.altMinAngle || 0;
          const maxAngle = condition.maxAngle || condition.altMaxAngle || 360;
          return angle >= minAngle && angle <= maxAngle;
        } else if (condition.type === 'distance') {
          const points = condition.points || condition.altPoints;
          if (!points) return false;
          const distance = calculateDistance(landmarks[points[0]], landmarks[points[1]]);
          if (distance === null) return false;
          return distance <= (condition.maxDistance || condition.altMaxDistance);
        }
        return false;
      });

      const altConditionsMet = pose.conditions.some((condition) => {
        if (condition.altPoints || condition.altMaxDistance || condition.altMinAngle) {
          if (condition.type === 'distance' && condition.altPoints) {
            const altDistance = calculateDistance(landmarks[condition.altPoints[0]], landmarks[condition.altPoints[1]]);
            return altDistance !== null && altDistance <= condition.altMaxDistance;
          } else if (condition.type === 'angle' && condition.altPoints) {
            const altAngle = calculateAngle(
              landmarks[condition.altPoints[0]],
              landmarks[condition.altPoints[1]],
              landmarks[condition.altPoints[2]]
            );
            if (altAngle === null) return false;
            const minAngle = condition.altMinAngle || 0;
            const maxAngle = condition.altMaxAngle || 360;
            return altAngle >= minAngle && altAngle <= maxAngle;
          }
        }
        return false;
      });

      return { isDetected: allConditionsMet || altConditionsMet, missing };
    };

    let poseType = 'unknown';
    let missingPoints = [];
    let poseCategory = 'unknown';
    let fullBodyVisible = false;

    for (const poseId in poseConfig) {
      const pose = poseConfig[poseId];
      const result = checkPose(pose);
      if (result.isDetected) {
        poseType = poseId;
        poseCategory = pose.category;
        missingPoints = result.missing.map((index) => names[index] || `landmark ${index}`);
        fullBodyVisible =
          result.missing.length <= (isBent ? 4 : pose.category === 'side' ? Math.ceil(pose.criticalLandmarks.length / 2) : 2);
        break;
      }
    }

    if (poseType === 'unknown') {
      const frontalMissing = getMissingLandmarks(criticalLandmarks.frontal, 0.1);
      missingPoints = frontalMissing.map((index) => names[index] || `landmark ${index}`);

      const leftSideMissing = getMissingLandmarks(criticalLandmarks.leftSide, 0.1);
      const rightSideMissing = getMissingLandmarks(criticalLandmarks.rightSide, 0.1);
      const isLeftSideVisible = leftSideMissing.length <= 3;
      const isRightSideVisible = rightSideMissing.length <= 3;

      if (landmarks[11] && landmarks[12]) {
        const shoulderDiff = landmarks[11].z - landmarks[12].z;
        const hipDiff = landmarks[23]?.z - landmarks[24]?.z || 0;

        if (Math.abs(shoulderDiff) > 0.03 || Math.abs(hipDiff) > 0.03 || isLeftSideVisible || isRightSideVisible) {
          if (shoulderDiff > 0.03 || isLeftSideVisible) {
            poseType = 'leftSide';
            poseCategory = 'side';
            const landmarksToCheck = isBent ? criticalLandmarks.bentLeftSide : criticalLandmarks.leftSide;
            missingPoints = getMissingLandmarks(landmarksToCheck, 0.1).map((index) => names[index] || `landmark ${index}`);
            fullBodyVisible = missingPoints.length <= 4;
          } else if (shoulderDiff < -0.03 || isRightSideVisible) {
            poseType = 'rightSide';
            poseCategory = 'side';
            const landmarksToCheck = isBent ? criticalLandmarks.bentRightSide : criticalLandmarks.rightSide;
            missingPoints = getMissingLandmarks(landmarksToCheck, 0.1).map((index) => names[index] || `landmark ${index}`);
            fullBodyVisible = missingPoints.length <= 4;
          } else {
            poseType = 'turned';
            poseCategory = 'side';
            missingPoints = getMissingLandmarks(criticalLandmarks.side, 0.1).map((index) => names[index] || `landmark ${index}`);
            fullBodyVisible = missingPoints.length <= 4;
          }
        } else if (frontalMissing.length <= 3) {
          poseType = 'frontal';
          poseCategory = 'frontal';
          fullBodyVisible = true;
        }
      } else if (isLeftSideVisible || isRightSideVisible) {
        if (isLeftSideVisible) {
          poseType = 'leftSide';
          poseCategory = 'side';
          missingPoints = getMissingLandmarks(criticalLandmarks.leftSide, 0.1).map((index) => names[index] || `landmark ${index}`);
          fullBodyVisible = missingPoints.length <= 4;
        } else if (isRightSideVisible) {
          poseType = 'rightSide';
          poseCategory = 'side';
          missingPoints = getMissingLandmarks(criticalLandmarks.rightSide, 0.1).map((index) => names[index] || `landmark ${index}`);
          fullBodyVisible = missingPoints.length <= 4;
        }
      }
    }

    return { poseType, poseCategory, missingPoints, fullBodyVisible };
  };

  const checkBodyVisibility = useCallback(async () => {
    const video = webcamRef.current?.video;
    if (!video || video.readyState < 3 || !poseLandmarkerRef.current || !isActive) {
      if (!isActive) return;
      console.log('Skipping visibility check:', {
        hasVideo: !!video,
        videoReadyState: video?.readyState,
        hasPoseLandmarker: !!poseLandmarkerRef.current
      });
      onVisibilityChange({
        poseType: 'unknown',
        poseCategory: 'unknown',
        fullBodyVisible: false,
        missingPoints: [],
        isTooClose: false,
        distance: null,
        distanceStatus: null,
        landmarks: [],
        message: 'Camera or pose landmarker not ready. Please ensure your webcam is active.'
      });
      return;
    }

    try {
      const results = await poseLandmarkerRef.current.detectForVideo(video, performance.now());
      let landmarks = results.landmarks?.[0] || [];
      if (landmarks.length === 0) {
        console.log('No landmarks detected, setting fullBodyVisible to false');
        onVisibilityChange({
          fullBodyVisible: false,
          poseType: 'unknown',
          poseCategory: 'unknown',
          missingPoints: [],
          isTooClose: false,
          distance: null,
          distanceStatus: null,
          landmarks: [],
          message: 'No body detected. Please ensure your full body is visible.'
        });
        return;
      }

      landmarks = smoothLandmarks(landmarks, landmarkHistoryRef.current, 3);

      const names = {
        0: 'nose',
        7: 'left ear',
        8: 'right ear',
        11: 'left shoulder',
        12: 'right shoulder',
        13: 'left elbow',
        14: 'right elbow',
        15: 'left wrist',
        16: 'right wrist',
        23: 'left hip',
        24: 'right hip',
        25: 'left knee',
        26: 'right knee',
        27: 'left ankle',
        28: 'right ankle',
        29: 'left heel',
        30: 'right heel',
        31: 'left foot index',
        32: 'right foot index'
      };

      // Strict full-body visibility check for initial detection (head to feet)
      const criticalLandmarksForFullBody = [0, 7, 8, 11, 12, 13, 14, 15, 16, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32];
      const missingForInitialCheck = criticalLandmarksForFullBody.filter((index) => {
        return !landmarks[index] || landmarks[index].visibility < 0.6;
      });

      let finalFullBodyVisible = missingForInitialCheck.length === 0;
      let message = '';

      if (!isFullBodyDetected && !initialCountdownActive) {
        // Preliminary check: waiting for full body detection
        if (!finalFullBodyVisible) {
          console.log('Preliminary check: Full body not fully visible.', {
            missing: missingForInitialCheck.map((index) => names[index] || `landmark ${index}`),
            missingCount: missingForInitialCheck.length,
            visibilityValues: criticalLandmarksForFullBody.map((index) => ({
              landmark: names[index] || `landmark ${index}`,
              visibility: landmarks[index]?.visibility?.toFixed(3) || 'missing'
            }))
          });
          message = 'Please ensure your full body from head to feet is visible.';
          onVisibilityChange({
            fullBodyVisible: false,
            poseType: 'unknown',
            poseCategory: 'unknown',
            missingPoints: missingForInitialCheck.map((index) => names[index] || `landmark ${index}`),
            isTooClose: false,
            distance: null,
            distanceStatus: null,
            landmarks: [],
            message
          });
          return;
        } else {
          console.log('Full body detected, starting countdown at', new Date().toISOString());
          setIsFullBodyDetected(true);
          setInitialCountdownActive(true);
          message = 'Now full body is detected! Get ready with your pose in 7 seconds.';
        }
      }

      // After full body is detected, proceed with pose classification and distance checks
      const { poseType, poseCategory, missingPoints } = classifyPose(landmarks);

      let distance = null;
      let distanceStatus = null;
      if (isFullBodyDetected) {
        try {
          if (landmarks.length > 0) {
            let torsoSize;
            const isBent =
              landmarks[25]?.y > landmarks[23]?.y ||
              landmarks[26]?.y > landmarks[24]?.y ||
              landmarks[15]?.y > landmarks[11]?.y ||
              landmarks[16]?.y > landmarks[12]?.y;

            if (poseCategory === 'side' || poseType.includes('Side')) {
              const shoulderY = landmarks[11]?.y || landmarks[12]?.y;
              const hipY = landmarks[23]?.y || landmarks[24]?.y;
              if (shoulderY && hipY) {
                torsoSize = Math.abs(shoulderY - hipY);
                if (isBent) torsoSize *= 1.4;
              }
            } else {
              const shoulderWidth = landmarks[11] && landmarks[12] ? Math.abs(landmarks[11].x - landmarks[12].x) : 0.3;
              const torsoHeight = landmarks[11] && landmarks[23] ? Math.abs(landmarks[11].y - landmarks[23].y) : 0.5;
              torsoSize = isBent ? Math.max(shoulderWidth * 1.4, torsoHeight * 0.7) : Math.max(shoulderWidth, torsoHeight * 0.5);
            }

            if (torsoSize) {
              const distanceMultiplier = isBent ? 40 : 30;
              distance = Math.min(100, Math.max(0, (0.25 / torsoSize) * distanceMultiplier));
              const minDistance = isBent ? 10 : 20;
              const maxDistance = isBent ? 98 : 90;

              const now = Date.now();
              if (now - lastDistanceWarningRef.current > 5000) {
                distanceStatus = distance < minDistance ? 'too close' : distance > maxDistance ? 'too far' : 'good distance';
                if (distanceStatus !== 'good distance') {
                  lastDistanceWarningRef.current = now;
                }
              } else {
                distanceStatus = 'good distance';
              }
            }
          }
        } catch (err) {
          console.error('Error in distance calculation:', err);
          distance = 50;
          distanceStatus = 'good distance';
        }
      }

      const isTooClose = distanceStatus === 'too close';

      if (initialCountdownActive && isFullBodyDetected) {
        // During countdown, maintain strict visibility check
        if (!finalFullBodyVisible) {
          console.log('Countdown active: Full body not fully visible.', {
            missing: missingForInitialCheck.map((index) => names[index] || `landmark ${index}`),
            missingCount: missingForInitialCheck.length,
            visibilityValues: criticalLandmarksForFullBody.map((index) => ({
              landmark: names[index] || `landmark ${index}`,
              visibility: landmarks[index]?.visibility?.toFixed(3) || 'missing'
            }))
          });
          message = 'Full body not fully visible. Please adjust your position.';
        } else {
          message = 'Get ready with your pose in 7 seconds.';
        }
      } else if (isFullBodyDetected && !initialCountdownActive && isCapturing) {
        // During 3-second capture
        message = 'Great, now we are capturing your pose for another 3 seconds.';
        finalFullBodyVisible = missingForInitialCheck.length <= 7;
        if (!finalFullBodyVisible && landmarks[11] && landmarks[12] && landmarks[23] && landmarks[24]) {
          const keyLandmarksVisible = [11, 12, 23, 24].every((index) => landmarks[index]?.visibility >= 0.1);
          if (keyLandmarksVisible) {
            finalFullBodyVisible = true;
            console.log('Capture phase: Full body visible due to key landmarks (shoulders and hips).');
          }
        }
        if (!finalFullBodyVisible) {
          console.log('Capture phase: Full body not fully visible.', {
            missing: missingForInitialCheck.map((index) => names[index] || `landmark ${index}`),
            missingCount: missingForInitialCheck.length,
            poseType,
            poseCategory
          });
          message = 'Full body not fully visible during capture. Please adjust your position.';
        }
      } else if (isCaptureComplete) {
        // After capture is complete
        message = 'Awesome pose! Capturing completed.';
        finalFullBodyVisible = missingForInitialCheck.length <= 7;
      }

      console.log('Visibility check result:', {
        fullBodyVisible: finalFullBodyVisible,
        poseType,
        poseCategory,
        missingLandmarks: missingPoints,
        isTooClose,
        distance: distance ? Math.round(distance) : null,
        distanceStatus,
        initialCountdownActive,
        isFullBodyDetected,
        isCapturing,
        isCaptureComplete,
        message
      });

      onVisibilityChange({
        fullBodyVisible: finalFullBodyVisible,
        poseType,
        poseCategory,
        missingPoints,
        isTooClose,
        distance,
        distanceStatus,
        landmarks,
        message
      });
    } catch (err) {
      console.error('Error checking body visibility:', err);
      onVisibilityChange({
        fullBodyVisible: false,
        poseType: 'unknown',
        poseCategory: 'unknown',
        missingPoints: ['Error in pose detection'],
        isTooClose: false,
        distance: null,
        distanceStatus: null,
        landmarks: [],
        message: 'Error in pose detection. Please try again.'
      });
    }
  }, [webcamRef, poseLandmarkerRef, onVisibilityChange, initialCountdownActive, isFullBodyDetected, isCapturing, isCaptureComplete, isActive]);

  useEffect(() => {
    if (!webcamRef.current || !poseLandmarkerRef.current || !isActive) {
      console.warn('useEffect skipped: webcamRef or poseLandmarkerRef not ready, or not active');
      return;
    }

    console.log('Starting preliminary full-body detection at', new Date().toISOString());

    // Preliminary check to detect full body before starting countdown
    preliminaryCheckIntervalRef.current = setInterval(async () => {
      if (isFullBodyDetected) {
        // Full body detected, start countdown and regular checks
        clearInterval(preliminaryCheckIntervalRef.current);
        console.log('Full body detected, starting 7-second countdown at', new Date().toISOString());
        visibilityCheckIntervalRef.current = setInterval(checkBodyVisibility, 500);
        countdownTimerRef.current = setTimeout(() => {
          console.log('Initial countdown finished, starting 3-second capture at', new Date().toISOString());
          setInitialCountdownActive(false);
          setIsCapturing(true);
          captureTimerRef.current = setTimeout(() => {
            console.log('Pose capture completed at', new Date().toISOString());
            setIsCapturing(false);
            setIsCaptureComplete(true);
            clearInterval(visibilityCheckIntervalRef.current);
          }, 3000);
        }, 7000);
        return;
      }

      await checkBodyVisibility(); // Run visibility check to update isFullBodyDetected
    }, 500);

    return () => {
      console.log('Cleaning up preliminary checks, visibility checks, countdown, and capture timers at', new Date().toISOString());
      clearInterval(preliminaryCheckIntervalRef.current);
      clearInterval(visibilityCheckIntervalRef.current);
      clearTimeout(countdownTimerRef.current);
      clearTimeout(captureTimerRef.current);
    };
  }, [checkBodyVisibility, webcamRef, poseLandmarkerRef, isFullBodyDetected]);

  return { checkBodyVisibility, initialCountdownActive, isFullBodyDetected, isCapturing, isCaptureComplete };
};

export default usePoseDetection;
